<script setup>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import TfPopupShell from './TfPopupShell.vue';
import {
  createUsuario,
  credenciaisUsuario,
  deleteUsuario,
  listAbas,
  listDepartamentos,
  listUsuarios,
  updateUsuario,
} from './usuariosApi';

const emit = defineEmits(['close']);

const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');

const ABAS_FALLBACK = [
  { id: 'assistente', label: 'Assistente' },
  { id: 'prompt', label: 'Prompt' },
  { id: 'conversas', label: 'Conversas' },
  { id: 'crm', label: 'CRM' },
  { id: 'calendario', label: 'Calendário' },
  { id: 'campanhas', label: 'Campanhas' },
  { id: 'participantes', label: 'Participantes' },
];

const view = ref('lista');
const loading = ref(false);
const saving = ref(false);
const err = ref('');
const statusHint = ref('');
const usuarios = ref([]);
const abasCatalogo = ref(ABAS_FALLBACK.map(a => a.id));
const deptosCatalogo = ref([]);

const editId = ref(null);
const form = ref(emptyForm());

function emptyForm() {
  return {
    nome: '',
    email: '',
    senha: '',
    senhaConf: '',
    role: 'agente',
    ativo: true,
    abas: [...ABAS_FALLBACK.map(a => a.id)],
    departamento_ids: [],
  };
}

const titulo = computed(() =>
  view.value === 'form'
    ? editId.value
      ? 'Editar usuário'
      : 'Novo usuário'
    : 'Usuários'
);

function labelAba(id) {
  const f = ABAS_FALLBACK.find(a => a.id === id);
  return f?.label || String(id);
}

function roleLabel(role) {
  const r = String(role || '').toLowerCase();
  if (r === 'admin' || r === 'administrator') return 'Administrador';
  return 'Atendente';
}

function abasList(u) {
  const raw = u?.abas || [];
  if (Array.isArray(raw) && raw.length) return raw.map(String);
  return [];
}

function deptoLabel(u) {
  const d = u?.departamentos;
  if (!d || (Array.isArray(d) && !d.length)) return null;
  if (Array.isArray(d)) {
    return d
      .map(x => (typeof x === 'string' ? x : x?.name || x?.nome))
      .filter(Boolean)
      .join(', ');
  }
  return String(d);
}

async function carregar() {
  loading.value = true;
  err.value = '';
  statusHint.value = '';
  try {
    const acc = Number(accountId.value);
    const user = currentUser.value;
    const [data, abasData, deptData] = await Promise.all([
      listUsuarios(acc, user),
      listAbas(acc, user).catch(() => null),
      listDepartamentos(acc, user).catch(() => null),
    ]);
    if (abasData?.abas?.length) abasCatalogo.value = abasData.abas.map(String);
    if (deptData?.departamentos) deptosCatalogo.value = deptData.departamentos;
    const list = Array.isArray(data) ? data : data?.usuarios || [];
    usuarios.value = list;
    let extra = '';
    if (data?.sync) {
      extra = ` · sync Atendimento: ${data.sync.totalAgents || 0} agent(s)`;
      if (data.sync.criados) extra += `, +${data.sync.criados} importado(s)`;
      if (data.sync.vinculados) extra += `, ${data.sync.vinculados} vínculo(s)`;
    }
    if (data?.aviso) extra += ` · aviso: ${data.aviso}`;
    statusHint.value = `${list.length} usuário(s)${extra}`;
  } catch (e) {
    err.value = e?.data?.erro || e?.data?.error || e?.message || 'Não foi possível carregar os usuários.';
    usuarios.value = [];
  } finally {
    loading.value = false;
  }
}

function mostrarNovo() {
  editId.value = null;
  form.value = emptyForm();
  form.value.abas = [...abasCatalogo.value];
  view.value = 'form';
  err.value = '';
}

function editar(u) {
  editId.value = u.id;
  const deptIds = (u.departamentos || [])
    .map(d => (typeof d === 'object' ? d.id : d))
    .filter(Boolean);
  form.value = {
    nome: u.nome || u.name || '',
    email: u.email || '',
    senha: '',
    senhaConf: '',
    role: String(u.role || '').toLowerCase() === 'admin' ? 'admin' : 'agente',
    ativo: u.ativo !== false,
    abas: abasList(u).length ? abasList(u) : [...abasCatalogo.value],
    departamento_ids: deptIds,
  };
  view.value = 'form';
  err.value = '';
}

function voltarLista() {
  view.value = 'lista';
  err.value = '';
}

function toggleAba(id) {
  const set = new Set(form.value.abas);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  form.value.abas = [...set];
}

function marcarTodasAbas(on) {
  form.value.abas = on ? [...abasCatalogo.value] : [];
}

function toggleDepto(id) {
  const set = new Set(form.value.departamento_ids.map(Number));
  const n = Number(id);
  if (set.has(n)) set.delete(n);
  else set.add(n);
  form.value.departamento_ids = [...set];
}

async function salvar() {
  err.value = '';
  const f = form.value;
  if (!f.nome.trim() || !f.email.trim()) {
    err.value = 'Nome e e-mail são obrigatórios.';
    return;
  }
  if (!editId.value) {
    if (!f.senha || f.senha.length < 8) {
      err.value = 'Senha com no mínimo 8 caracteres.';
      return;
    }
    if (f.senha !== f.senhaConf) {
      err.value = 'Confirmação de senha não confere.';
      return;
    }
  } else if (f.senha) {
    if (f.senha.length < 8) {
      err.value = 'Senha com no mínimo 8 caracteres.';
      return;
    }
    if (f.senha !== f.senhaConf) {
      err.value = 'Confirmação de senha não confere.';
      return;
    }
  }

  const abas =
    f.role === 'admin' ? [...abasCatalogo.value] : f.abas;

  saving.value = true;
  const acc = Number(accountId.value);
  const user = currentUser.value;
  try {
    if (editId.value) {
      const body = {
        nome: f.nome.trim(),
        role: f.role,
        abas,
        ativo: f.ativo,
        departamento_ids: f.departamento_ids,
        sync_chatwoot: true,
      };
      if (f.senha) body.senha = f.senha;
      await updateUsuario(acc, user, editId.value, body);
      useAlert('Usuário atualizado.');
    } else {
      await createUsuario(acc, user, {
        nome: f.nome.trim(),
        email: f.email.trim(),
        senha: f.senha,
        role: f.role,
        abas,
        departamento_ids: f.departamento_ids,
        sync_chatwoot: true,
      });
      useAlert('Usuário criado.');
    }
    view.value = 'lista';
    await carregar();
  } catch (e) {
    err.value = e?.data?.erro || e?.data?.error || e?.message || 'Falha ao salvar usuário.';
  } finally {
    saving.value = false;
  }
}

async function excluir(u) {
  if (!window.confirm(`Excluir ${u.nome || u.email}?`)) return;
  try {
    await deleteUsuario(Number(accountId.value), currentUser.value, u.id);
    useAlert('Usuário removido.');
    await carregar();
  } catch (e) {
    useAlert(e?.data?.erro || e?.data?.error || e?.message || 'Não foi possível excluir.');
  }
}

async function copiarAcesso(u) {
  try {
    const data = await credenciaisUsuario(
      Number(accountId.value),
      currentUser.value,
      u.id
    );
    const text =
      data?.texto ||
      (data?.email
        ? `E-mail: ${data.email}\nSenha: ${data.senha || ''}`
        : u.email || '');
    await navigator.clipboard.writeText(text);
    useAlert('Acesso copiado.');
  } catch (_) {
    useAlert('Não foi possível copiar.');
  }
}

carregar();
</script>

<template>
  <TfPopupShell :label="titulo" wide @close="emit('close')">
    <div class="wrap">
      <template v-if="view === 'lista'">
        <div class="toolbar">
          <div>
            <p class="eyebrow">Equipe</p>
            <h2 class="title">Usuários</h2>
            <p class="hint">
              Acessos do painel. Departamentos: edite o usuário ou use Filas de atendimento no Assistente.
            </p>
            <p v-if="statusHint" class="status">{{ statusHint }}</p>
          </div>
          <button type="button" class="btn" @click="mostrarNovo">Novo usuário</button>
        </div>
        <p v-if="err" class="err">{{ err }}</p>
        <p v-else-if="loading" class="hint">Carregando…</p>
        <div v-else class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Perfil</th>
                <th>Departamentos</th>
                <th>Abas</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in usuarios" :key="u.id || u.email">
                <td class="nome">{{ u.nome || '—' }}</td>
                <td class="email">{{ u.email || '—' }}</td>
                <td><span class="badge">{{ roleLabel(u.role) }}</span></td>
                <td>
                  <span v-if="deptoLabel(u)" class="badge">{{ deptoLabel(u) }}</span>
                  <span
                    v-else
                    class="badge off"
                    title="Atribua em Editar ou em Filas de atendimento"
                  >Sem fila</span>
                </td>
                <td>
                  <div class="abas">
                    <span
                      v-for="a in abasCatalogo"
                      :key="a"
                      :class="{ off: !abasList(u).includes(a) }"
                    >{{ labelAba(a) }}</span>
                  </div>
                </td>
                <td>
                  <div class="actions">
                    <button type="button" title="Copiar e-mail e senha" @click="copiarAcesso(u)">
                      Copiar acesso
                    </button>
                    <button type="button" @click="editar(u)">Editar</button>
                    <button
                      v-if="Number(u.id) !== 1"
                      type="button"
                      class="danger"
                      @click="excluir(u)"
                    >
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!usuarios.length">
                <td colspan="6" class="empty">Nenhum usuário.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <template v-else>
        <p class="eyebrow">Gestão de acessos</p>
        <h2 class="title">{{ editId ? 'Editar usuário' : 'Novo usuário' }}</h2>
        <p class="hint">Identidade, senha, perfil e permissões</p>
        <form class="form" @submit.prevent="salvar">
          <div class="grid2">
            <label class="field">
              <span>Nome</span>
              <input v-model="form.nome" type="text" required placeholder="Nome completo" autocomplete="name" />
            </label>
            <label class="field">
              <span>E-mail</span>
              <input
                v-model="form.email"
                type="email"
                required
                placeholder="email@empresa.com"
                autocomplete="email"
                :disabled="!!editId"
              />
            </label>
          </div>
          <div class="grid2">
            <label class="field">
              <span>{{ editId ? 'Nova senha (opcional)' : 'Senha' }}</span>
              <input
                v-model="form.senha"
                type="password"
                :required="!editId"
                minlength="8"
                :placeholder="editId ? 'Deixe em branco para manter' : 'Mín. 8 caracteres'"
                autocomplete="new-password"
              />
            </label>
            <label class="field">
              <span>Confirmar senha</span>
              <input
                v-model="form.senhaConf"
                type="password"
                :required="!editId || !!form.senha"
                minlength="8"
                placeholder="Repita a senha"
                autocomplete="new-password"
              />
            </label>
          </div>

          <div class="roles" role="radiogroup" aria-label="Perfil">
            <button
              type="button"
              class="role"
              :class="{ active: form.role === 'agente' }"
              @click="form.role = 'agente'"
            >
              <strong>Atendente</strong>
              <span>Só as abas liberadas · perfil agent</span>
            </button>
            <button
              type="button"
              class="role"
              :class="{ active: form.role === 'admin' }"
              @click="form.role = 'admin'"
            >
              <strong>Administrador</strong>
              <span>Tudo liberado · administrator</span>
            </button>
          </div>

          <div v-if="form.role === 'agente'" class="abas-block">
            <div class="abas-head">
              <span>Abas permitidas</span>
              <div class="abas-btns">
                <button type="button" @click="marcarTodasAbas(true)">Todas</button>
                <button type="button" @click="marcarTodasAbas(false)">Nenhuma</button>
              </div>
            </div>
            <div class="abas-grid">
              <label v-for="a in abasCatalogo" :key="a" class="aba-check">
                <input
                  type="checkbox"
                  :checked="form.abas.includes(a)"
                  @change="toggleAba(a)"
                />
                {{ labelAba(a) }}
              </label>
            </div>
          </div>

          <div v-if="deptosCatalogo.length" class="abas-block">
            <div class="abas-head">
              <span>Departamentos</span>
            </div>
            <div class="abas-grid">
              <label
                v-for="d in deptosCatalogo"
                :key="d.id"
                class="aba-check"
              >
                <input
                  type="checkbox"
                  :checked="form.departamento_ids.map(Number).includes(Number(d.id))"
                  @change="toggleDepto(d.id)"
                />
                {{ d.name || d.nome }}
              </label>
            </div>
          </div>

          <p v-if="err" class="err">{{ err }}</p>
          <div class="form-actions">
            <button type="button" class="btn ghost" @click="voltarLista">Voltar</button>
            <button type="submit" class="btn" :disabled="saving">
              {{ saving ? 'Salvando…' : editId ? 'Salvar' : 'Criar usuário' }}
            </button>
          </div>
        </form>
      </template>
    </div>
  </TfPopupShell>
</template>

<style scoped>
.wrap {
  padding: 24px 28px 28px;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.eyebrow {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.title {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}
.hint {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}
.status {
  margin: 6px 0 0;
  font-size: 12px;
  color: #64748b;
}
.err {
  margin: 0 0 10px;
  font-size: 13px;
  color: #b91c1c;
}
.btn {
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px;
  background: #0f172a;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
}
.btn.ghost {
  background: #f1f5f9;
  color: #334155;
}
.btn:disabled {
  opacity: 0.6;
  cursor: wait;
}
.table-wrap {
  overflow: auto;
  flex: 1;
  min-height: 0;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.table th {
  text-align: left;
  padding: 10px 12px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  position: sticky;
  top: 0;
  z-index: 1;
}
.table td {
  padding: 10px 12px;
  border-top: 1px solid #f1f5f9;
  vertical-align: middle;
  color: #0f172a;
}
.nome {
  font-weight: 600;
}
.email {
  color: #475569;
  font-size: 12px;
}
.empty {
  text-align: center;
  color: #94a3b8;
  padding: 24px !important;
}
.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 11px;
  font-weight: 600;
}
.badge.off {
  background: #f1f5f9;
  color: #94a3b8;
}
.abas {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 280px;
}
.abas span {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 6px;
  background: #ecfdf5;
  color: #065f46;
  font-weight: 600;
}
.abas span.off {
  background: #f8fafc;
  color: #cbd5e1;
  text-decoration: line-through;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.actions button {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
}
.actions button.danger {
  color: #b91c1c;
  border-color: #fecaca;
}
.form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 12px;
  overflow: auto;
  flex: 1;
  min-height: 0;
  padding-right: 4px;
}
.grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
@media (max-width: 640px) {
  .grid2,
  .roles {
    grid-template-columns: 1fr;
  }
  .toolbar {
    flex-direction: column;
  }
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.field input {
  height: 40px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 400;
  color: #0f172a;
}
.field input:focus {
  outline: none;
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.25);
}
.field input:disabled {
  background: #f8fafc;
  color: #94a3b8;
}
.roles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.role {
  text-align: left;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
  cursor: pointer;
}
.role.active {
  border-color: #0f172a;
  background: #f8fafc;
  box-shadow: 0 0 0 1px #0f172a;
}
.role strong {
  display: block;
  font-size: 13px;
  color: #0f172a;
  margin-bottom: 4px;
}
.role span {
  font-size: 12px;
  color: #64748b;
}
.abas-block {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
}
.abas-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.abas-btns {
  display: flex;
  gap: 6px;
}
.abas-btns button {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  cursor: pointer;
}
.abas-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 8px;
}
.aba-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 4px;
  padding-bottom: 8px;
}
</style>
